import { ensureStatsPvId } from '@/utils/statsTelemetry'

let telemetryInstalled = false
let statusObserver = null
let lastLegacyPositionPayload = ''
let slotToConfigKeys = {}
let scrollTimer = null
let resizeTimer = null
let statusTimer = null
let lastClicked = {
	adInstanceId: null,
	slotId: null,
}

function callAndroid(method, payload) {
	if (typeof window === 'undefined') return false

	const bridge = window.JSCallAndroid
	const fn = bridge && bridge[method]
	if (typeof fn !== 'function') return false

	try {
		fn.call(bridge, JSON.stringify(payload))
		return true
	} catch (error) {
		console.warn(`[AdTelemetryV2] ${method} failed`, error)
		return false
	}
}

function debounce(timerName, callback, delay) {
	if (timerName === 'scroll' && scrollTimer) clearTimeout(scrollTimer)
	if (timerName === 'resize' && resizeTimer) clearTimeout(resizeTimer)
	if (timerName === 'status' && statusTimer) clearTimeout(statusTimer)

	const timer = setTimeout(callback, delay)
	if (timerName === 'scroll') scrollTimer = timer
	if (timerName === 'resize') resizeTimer = timer
	if (timerName === 'status') statusTimer = timer
}

function indexAdConfig(adsConfig = {}) {
	slotToConfigKeys = {}
	Object.entries(adsConfig || {}).forEach(([key, value]) => {
		if (!value || typeof value !== 'object') return
		const slotId = value['data-ad-slot']
		if (!slotId) return
		slotToConfigKeys[slotId] = slotToConfigKeys[slotId] || []
		slotToConfigKeys[slotId].push(key)
	})
}

function visibleAreaRatio(rect) {
	const visibleWidth = Math.max(0, Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0))
	const visibleHeight = Math.max(0, Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0))
	const area = Math.max(0, rect.width * rect.height)
	const visibleArea = visibleWidth * visibleHeight
	return area > 0 ? Math.round((visibleArea / area) * 100) : 0
}

function collectLegacyPositions() {
	return Array.from(document.querySelectorAll('ins.adsbygoogle'))
		.filter((element) => element instanceof HTMLElement)
		.map((element) => element.getBoundingClientRect())
		.filter((rect) => rect.width > 0 && rect.height > 0 && rect.bottom >= 0 && rect.top <= window.innerHeight)
		.map((rect) => ({
			insX: Math.round(rect.left),
			insY: Math.round(rect.top),
			insHeight: Math.round(rect.height),
			windowWidth: window.innerWidth,
			windowHeight: window.innerHeight,
		}))
}

function sendLegacyAdPosition() {
	const positions = collectLegacyPositions()
	const payload = JSON.stringify(positions)
	if (payload === lastLegacyPositionPayload) return
	lastLegacyPositionPayload = payload
	callAndroid('adPosition', positions)
}

function collectAds() {
	return Array.from(document.querySelectorAll('ins.adsbygoogle'))
		.filter((element) => element instanceof HTMLElement)
		.map((element, index) => {
			const rect = element.getBoundingClientRect()
			const iframe = element.querySelector('iframe')
			const iframeRect = iframe?.getBoundingClientRect()
			const slotId = element.getAttribute('data-ad-slot')
			const status = element.getAttribute('data-ad-status') || element.getAttribute('data-adsbygoogle-status') || (iframe ? 'iframe_inserted' : 'pending')
			const computedStyle = window.getComputedStyle(element)

			return {
				adInstanceId: `ad_${index + 1}`,
				domIndex: index,
				network: slotId ? 'adsense' : 'auto_ads',
				adType: slotId ? 'manual_inline_lazy' : 'auto_ads',
				sourceComponent: element.closest('[data-component]')?.getAttribute('data-component') || null,
				configKeys: slotId ? slotToConfigKeys[slotId] || [] : [],
				slotId,
				clientId: element.getAttribute('data-ad-client') || '',
				adFormat: element.getAttribute('data-ad-format'),
				fullWidthResponsive: element.getAttribute('data-full-width-responsive'),
				adStatus: status,
				adsbygoogleStatus: element.getAttribute('data-adsbygoogle-status'),
				hasIframe: Boolean(iframe),
				iframeCount: element.querySelectorAll('iframe').length,
				iframeWidth: iframeRect ? Math.round(iframeRect.width) : 0,
				iframeHeight: iframeRect ? Math.round(iframeRect.height) : 0,
				viewportX: Math.round(rect.left),
				viewportY: Math.round(rect.top),
				pageX: Math.round(rect.left + window.scrollX),
				pageY: Math.round(rect.top + window.scrollY),
				insWidth: Math.round(rect.width),
				insHeight: Math.round(rect.height),
				isInViewport: rect.bottom > 0 && rect.top < window.innerHeight && rect.right > 0 && rect.left < window.innerWidth,
				visibilityRatio: visibleAreaRatio(rect),
				positionMode: computedStyle.position === 'fixed' ? 'fixed' : 'document_flow',
				className: element.className || '',
			}
		})
}

function buildPayload(eventType, triggerSource, extra = {}) {
	const ads = collectAds()
	return {
		version: 2,
		pageViewId: ensureStatsPvId(),
		stats_pv_id: ensureStatsPvId(),
		eventType,
		triggerSource,
		timestamp: Date.now(),
		timestampIso: new Date().toISOString(),
		page: {
			host: window.location.host,
			path: window.location.pathname,
			query: window.location.search,
			url: window.location.href,
			title: document.title,
			referrer: document.referrer || null,
		},
		viewport: {
			windowWidth: window.innerWidth,
			windowHeight: window.innerHeight,
			scrollX: window.scrollX,
			scrollY: window.scrollY,
			documentWidth: document.documentElement.scrollWidth,
			documentHeight: document.documentElement.scrollHeight,
			devicePixelRatio: window.devicePixelRatio,
		},
		summary: {
			totalAds: ads.length,
			visibleAds: ads.filter((ad) => ad.isInViewport).length,
			filledAds: ads.filter((ad) => ad.hasIframe || ad.adStatus === 'filled' || ad.adStatus === 'loaded').length,
			unfilledAds: ads.filter((ad) => ad.adStatus === 'unfilled').length,
			clickedAdInstanceId: lastClicked.adInstanceId,
			clickedSlotId: lastClicked.slotId,
		},
		ads,
		...extra,
	}
}

export function emitAdTelemetryV2(eventType, triggerSource, extra = {}) {
	if (typeof window === 'undefined') return

	sendLegacyAdPosition()
	const payload = buildPayload(eventType, triggerSource, extra)
	if (!callAndroid('adTelemetryV2', payload) && ['localhost', '127.0.0.1', '0.0.0.0'].includes(window.location.hostname)) {
		console.log('[AdTelemetryV2]', payload.eventType, payload.summary)
	}
}

export function scheduleAdTelemetryV2(eventType, triggerSource, delay = 250, extra = {}) {
	if (typeof window === 'undefined') return
	debounce('status', () => emitAdTelemetryV2(eventType, triggerSource, extra), delay)
}

export function emitAdClickTelemetryV2(adElement, triggerSource, extra = {}) {
	if (typeof window === 'undefined') return

	const ads = Array.from(document.querySelectorAll('ins.adsbygoogle'))
	const ins = adElement?.closest?.('ins.adsbygoogle') || adElement
	const index = ads.indexOf(ins)
	lastClicked = {
		adInstanceId: index >= 0 ? `ad_${index + 1}` : 'ad_unknown',
		slotId: ins?.getAttribute?.('data-ad-slot') || null,
	}
	emitAdTelemetryV2('click', triggerSource, extra)
}

function observeAdStatusChanges() {
	if (!window.MutationObserver || !document.body || statusObserver) return

	statusObserver = new MutationObserver((records) => {
		const changed = records.some((record) => {
			if (record.target instanceof HTMLElement && record.target.matches('ins.adsbygoogle')) return true
			return Array.from(record.addedNodes).some((node) => {
				return node instanceof HTMLElement && (node.matches('ins.adsbygoogle') || Boolean(node.querySelector('ins.adsbygoogle')))
			})
		})

		if (changed) {
			scheduleAdTelemetryV2('status_change', 'ad_dom_mutation')
		}
	})

	statusObserver.observe(document.body, {
		childList: true,
		subtree: true,
		attributes: true,
		attributeFilter: ['data-ad-status', 'data-adsbygoogle-status', 'style', 'class'],
	})
}

export function initAdTelemetryV2(adsConfig = {}) {
	if (typeof window === 'undefined') return

	indexAdConfig(adsConfig)
	if (telemetryInstalled) return
	telemetryInstalled = true

	window.addEventListener('scroll', () => {
		debounce('scroll', () => emitAdTelemetryV2('scroll', 'window_scroll'), 300)
	}, { passive: true })
	window.addEventListener('resize', () => {
		debounce('resize', () => emitAdTelemetryV2('resize', 'window_resize'), 500)
	})
	window.addEventListener('pagehide', () => emitAdTelemetryV2('pagehide', 'pagehide'))

	observeAdStatusChanges()
	window.setTimeout(() => emitAdTelemetryV2('init', 'init_ad_telemetry_v2'), 1000)
}
